import { CommonUtility } from '@utility/common';
import { CacheUtility } from '@utility/cache';
import { APIPath } from '../constants/api';
import { BaseService } from './base';

class Item {
  unassignedCount = params => {
    const url = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.scmUnassignedItemCount}/?${url}`);
  };

  unassignedItems = params => {
    const url = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.scmUnassignedItems}/?${url}`);
  };

  unassignedChildren = (id, params) => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.scmUnassignedChildren}/${id}?${query}`);
  };

  removeAssignedItems = data =>
    BaseService.put(`${APIPath.unassignedItem}`, data);

  assignItems = obj => BaseService.post(APIPath.assignItems, obj);

  update = obj => BaseService.put(APIPath.assignItems, obj);

  addItemAlias = obj => BaseService.post(APIPath.aliasItemSupplier, obj);

  itemAliasMarkPrimary = (alias, obj) => {
    const encoded = encodeURIComponent(alias);
    return BaseService.put(
      `${APIPath.aliasItemSupplier}/${encoded}/primary`,
      obj,
    );
  };

  supplierItems = async (supplierId, params, cacheResult = false) => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.supplierItems}/${supplierId}?${query}`;
    if (cacheResult) {
      const cache = CacheUtility.get(url);
      if (cache) {
        return cache;
      }
    }
    const result = await BaseService.get(url);
    if (cacheResult) {
      CacheUtility.save(url, result, 0.3);
    }
    return result;
  };

  purchaseOrderItems = (supplierId, params) => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.createPOItems}/${supplierId}?${query}`;
    return BaseService.get(url);
  };

  getItemLots = itemId => {
    const url = `${APIPath.items}/${itemId}/lots`;
    return BaseService.get(url);
  };

  itemDropdown = (regionId, params, signal?: AbortSignal) => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.itemDropdown}/${regionId}?${query}`,
      false,
      true,
      signal,
    );
  };

  marketplaceSyncToggle = item =>
    BaseService.put(APIPath.syncMarketplace, item);

  globalItems = params => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.globalItems}?${query}`;
    return BaseService.get(url);
  };

  globalItemsUpdate = data => BaseService.put(APIPath.globalItemsUpdate, data);

  deleteAttribute(itemId, alias) {
    const encoded = encodeURIComponent(alias);
    return BaseService.remove(`${APIPath.itemAttribute}/${itemId}/${encoded}`);
  }

  catalogRestockList = params => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.listCatalogRestock}?${query}`;
    return BaseService.get(url);
  };

  exportGlobalItems = params => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.globalItemsSpreadsheet}?${query}`;
    return BaseService.get(url);
  };

  getItemAttribute = id => BaseService.get(`${APIPath.scmAttribute}/${id}`);

  getItemAttributeByUpc = upc =>
    BaseService.get(`${APIPath.scmAttribute}/upc/${upc}`);

  updateManualParameters = data =>
    BaseService.put(APIPath.setManualParameters, data);

  updateMinimumStock = (id, data) =>
    BaseService.put(`${APIPath.minimumStock}/${id}`, data);

  getAttributesTemplate = imperialFlag =>
    BaseService.get(
      `${APIPath.attributesTemplate}?unit=${
        imperialFlag ? 'imperial' : 'metric'
      }`,
    );

  getMarketplaceCategory = id =>
    BaseService.get(`${APIPath.marketplaceCategory}/${id}`);

  getItemShipments = (id, params, signal?: AbortSignal) => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `items/${id}/shipments?${query}`,
      false,
      true,
      signal,
    );
  };

  saveAttributes = data => BaseService.post(APIPath.itemAttributes, data);

  deleteItemAttribute(itemId, alias) {
    const encoded = encodeURIComponent(alias);
    return BaseService.remove(`${APIPath.itemAttribute}/${itemId}/${encoded}`);
  }

  getItemLogs = id => BaseService.get(`${APIPath.itemLogs}/${id}`);

  updateItemTag = (id, data) =>
    BaseService.put(`${APIPath.itemTags}/${id}`, data);

  updateItemPrediction = (id, data) =>
    BaseService.put(`${APIPath.catalogPrediction}/${id}`, data);

  fetchCatalogCreateDescriptions = data =>
    BaseService.post(APIPath.catalogCreateDescriptions, data);

  fetchCatalogUpdateDescriptions = data =>
    BaseService.put(APIPath.catalogUpdateDescriptions, data);

  fetchCatalogDeleteTitle = (itemId, marketplaceId) =>
    BaseService.remove(`${APIPath.catalog}/${itemId}/title/${marketplaceId}`);

  updateAttributes = (itemId, data) =>
    BaseService.put(`${APIPath.catalog}/${itemId}/packageDimensions`, data);

  fetchCatalogDeleteDescription = (itemId, marketplaceId) =>
    BaseService.remove(
      `${APIPath.catalog}/${itemId}/descriptions/${marketplaceId}`,
    );
}

const ItemService = new Item();
Object.freeze(ItemService);
export default ItemService;
